import React, { useEffect, useState } from 'react';
import { changeRequestStatus, getServiceRequest } from '../../services/dataService';
import { IMAGE_URL } from '../../services/apiGateway';
import toast from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import { getSecureItem } from '../../services/SecureStorage';

function ServiceRequestAndPermissions() {

    const isAdmin = getSecureItem("IsAdmin");

    const departmentIds = getSecureItem("DepartmentId") || ""; // Retrieve the value or default to an empty string
    const Dep = departmentIds.split(",")[0] || null; // Split and get the first ID, or set to null if not found
    const UID = getSecureItem('userId');
    const location = useLocation();
    const [ViaDashBoard, setViaDashBoard] = useState(() => location.state?.pending || []);
    const [allServices, setAllServices] = useState([]);
    const [allServicesDoc, setAllServicesDoc] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [modalAction, setModalAction] = useState('');
    const [modalRemarks, setModalRemarks] = useState('');
    const [expandedRemarks, setExpandedRemarks] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        department: '',
        category: '',
        profitCenter: '',
        startDate: '',
        endDate: ''
    });


    const fetchServiceReq = async () => {
        try {
            const res = await getServiceRequest(UID);

            // Extract request and document data
            const serviceData = res?.data?.data?.[0] || [];
            const documentData = res?.data?.data?.[1] || [];

            // Filter based on admin or department
            const details = isAdmin == 1
                ? serviceData
                : serviceData.filter((el) => el.DepartmentId === Dep);

            // Determine whether to use dashboard-passed data
            const useDashboardData = Array.isArray(ViaDashBoard) && ViaDashBoard.length > 0;

            // Set states
            setAllServices(useDashboardData ? ViaDashBoard : details);
            setFilteredServices(useDashboardData ? ViaDashBoard : details);
            setAllServicesDoc(documentData);

        } catch (error) {
            console.error("Error fetching service request", error);
        }
    };
    useEffect(() => {
        fetchServiceReq();
    }, [UID]);

    // Filter services based on search query and filters
    useEffect(() => {
        let filtered = allServices;

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(service =>
                service.EmployeeName.toLowerCase().includes(query) ||
                service.EmpCode.toLowerCase().includes(query) ||
                service.TypeName.toLowerCase().includes(query)
            );
        }

        if (filters.department) {
            filtered = filtered.filter(service =>
                service.DepartmentName.toLowerCase().includes(filters.department.toLowerCase())
            );
        }

        if (filters.category) {
            filtered = filtered.filter(service =>
                service.CategoryName.toLowerCase().includes(filters.category.toLowerCase())
            );
        }

        if (filters.profitCenter) {
            filtered = filtered.filter(service =>
                service.ProfitCenterName.toLowerCase().includes(filters.profitCenter.toLowerCase())
            );
        }

        if (filters.startDate && filters.endDate) {
            filtered = filtered.filter(service => {
                const serviceDate = new Date(service.Date);
                const start = new Date(filters.startDate);
                const end = new Date(filters.endDate);
                return serviceDate >= start && serviceDate <= end;
            });
        }

        setFilteredServices(filtered);
    }, [searchQuery, filters, allServices]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const clearFilters = () => {
        setSearchQuery('');
        setFilters({
            department: '',
            category: '',
            profitCenter: '',
            startDate: '',
            endDate: ''
        });
    };

    const openModal = (request, action) => {
        setSelectedRequest(request);
        setModalAction(action);
        setModalRemarks('');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedRequest(null);
        setModalAction('');
        setModalRemarks('');
    };

    const handleStatusChange = async () => {
        if (!selectedRequest || !modalRemarks.trim()) {
            alert('Please enter remarks before proceeding.');
            return;
        }

        const data = {
            UserId: UID,
            UserRemarks: modalRemarks,
            Status: modalAction === 'approve' ? '1' : '2',
            EmpId: selectedRequest.EmpId,
            Id: selectedRequest.RequestId
        };

        try {
            const response = await changeRequestStatus(data);

            setAllServices(prev =>
                prev.map(service =>
                    service.RequestId === selectedRequest.RequestId
                        ? { ...service, ServiceStatus: modalAction === 'approve' ? '1' : '2', UserRemarks: modalRemarks }
                        : service
                )
            );

            closeModal();
            toast.success(`Request ${modalAction === 'approve' ? 'approved' : 'rejected'} successfully!`);
        } catch (error) {
            console.error("Error changing request status:", error);
            toast.error('Error updating request status. Please try again.');
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case '0':
                return <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-lg font-medium">Pending</span>;
            case '1':
                return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-lg font-medium">Approved</span>;
            case '2':
                return <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-lg font-medium">Rejected</span>;
            default:
                return <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-lg font-medium">Unknown</span>;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case '0': return 'bg-yellow-50 hover:bg-yellow-100';
            case '1': return 'bg-green-50 hover:bg-green-100';
            case '2': return 'bg-red-50 hover:bg-red-100';
            default: return 'bg-white hover:bg-gray-50';
        }
    };

    const getDocumentLinks = (requestId, documents) => {
        return documents
            .filter(doc => doc.RequsetId === requestId)
            .map(doc => (
                <a
                    key={doc.FileId}
                    href={`${IMAGE_URL}/${doc.Documents}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 underline mr-2 mb-2 text-lg"
                >
                    <span className="mr-1">📄</span>
                    {doc.Documents.split('_').pop()}
                </a>
            ));
    };

    const toggleRemarkExpansion = (requestId) => {
        setExpandedRemarks(prev => ({
            ...prev,
            [requestId]: !prev[requestId]
        }));
    };

    const truncateText = (text, maxLength = 100) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const getUniqueValues = (key) => {
        return [...new Set(allServices.map(service => service[key]))].filter(Boolean);
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-lg shadow-md">
                <div className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                        {/* Left Section: Breadcrumb + Heading */}
                        <div className="flex items-center gap-4 flex-wrap">
                            <Breadcrumb title={"Service And Permission Requests"} />
                            <h2 className="text-2xl font-semibold text-gray-800"></h2>
                        </div>

                        {/* Right Section: Button (conditionally rendered) */}
                        {ViaDashBoard.length >= 0 && (
                            <Link to="/">
                                <button className="btn btn-danger">Back to Dashboard</button>
                            </Link>
                        )}
                    </div>

                    {/* Filters Section */}
                    <div className="flex items-end gap-4 mb-6 flex-wrap">
                        <div className="min-w-[200px]">
                            <label className="block text-lg font-medium text-gray-700 mb-1">Search</label>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-lg"
                                placeholder="Search by name, code, or type..."
                            />
                        </div>
                        {isAdmin == 1 &&
                            <div className="min-w-[150px]">
                                <label className="block text-lg font-medium text-gray-700 mb-1">Department</label>
                                <select
                                    value={filters.department}
                                    onChange={(e) => handleFilterChange('department', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-lg"
                                >
                                    <option value="">All Departments</option>
                                    {getUniqueValues('DepartmentName').map(dept => (
                                        <option key={dept} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>
                        }
                        <div className="min-w-[150px]">
                            <label className="block text-lg font-medium text-gray-700 mb-1">Category</label>
                            <select
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-lg"
                            >
                                <option value="">All Categories</option>
                                {getUniqueValues('CategoryName').map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[150px]">
                            <label className="block text-lg font-medium text-gray-700 mb-1">Profit Center</label>
                            <select
                                value={filters.profitCenter}
                                onChange={(e) => handleFilterChange('profitCenter', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-lg"
                            >
                                <option value="">All Profit Centers</option>
                                {getUniqueValues('ProfitCenterName').map(pc => (
                                    <option key={pc} value={pc}>{pc}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[150px]">
                            <label className="block text-lg font-medium text-gray-700 mb-1">From</label>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-lg"
                            />
                        </div>
                        <div className="min-w-[150px]">
                            <label className="block text-lg font-medium text-gray-700 mb-1">To</label>
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-lg"
                            />
                        </div>
                        <button
                            onClick={clearFilters}
                            className="btn btn-danger"
                        >
                            Clear Filters
                        </button>
                    </div>

                    <div className="text-lg text-gray-600 mb-4">
                        Showing {filteredServices.length} of {allServices.length} requests
                    </div>
                </div>

                {/* Table Section */}
                <div className="max-h-[500px] overflow-auto">
                    {filteredServices.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500 text-lg">No service requests found.</p>
                        </div>
                    ) : (
                        <table className="w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[60px]">Sl No</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[180px]">Employee</th>
                                    {isAdmin == 1 &&
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[120px]">Department</th>}
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[120px]">Category</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[120px]">Profit Center</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[120px]">Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[400px]">Employee Remarks</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[100px]">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[180px]">Documents</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[100px]">duration</th> 
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[100px]">Reason</th> 
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-[180px]">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredServices.map((service, index) => (
                                    <tr key={service.RequestId} className={`transition-colors ${getStatusColor(service.ServiceStatus)}`}>
                                        <td className="px-4 py-4 whitespace-nowrap text-lg text-gray-900">{index + 1}</td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-lg font-medium text-gray-900">{service.EmployeeName}</div>
                                            <div className="text-xs text-gray-500">Code: {service.EmpCode}</div>
                                        </td>
                                        {isAdmin == 1 &&
                                            <td className="px-4 py-4 whitespace-nowrap text-lg text-gray-900">{service.DepartmentName}</td>}
                                        <td className="px-4 py-4 whitespace-nowrap text-lg text-gray-900">{service.CategoryName}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-lg text-gray-900">{service.ProfitCenterName}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-lg text-gray-900">{service.TypeName}</td>
                                        <td className="px-4 py-4 max-w-[400px] overflow-x-auto">
                                            <div className="text-lg text-gray-900">
                                                {service.EmpRemark && service.EmpRemark.length > 100 ? (
                                                    <div>
                                                        {expandedRemarks[service.RequestId]
                                                            ? service.EmpRemark
                                                            : truncateText(service.EmpRemark, 100)
                                                        }
                                                        <button
                                                            onClick={() => toggleRemarkExpansion(service.RequestId)}
                                                            className="ml-2 text-blue-600 hover:text-blue-800 text-lg underline"
                                                        >
                                                            {expandedRemarks[service.RequestId] ? 'View Less' : 'View More'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    service.EmpRemark || 'No remarks'
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-lg text-gray-900">
                                            {new Date(service.Date).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex flex-wrap">
                                                {getDocumentLinks(service.RequestId, allServicesDoc)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                           {service.Duration}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                           {service.Reason}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-lg font-medium">
                                            {service.ServiceStatus === '0' ? (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => openModal(service, 'approve')}
                                                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-lg transition-colors"
                                                    >
                                                        ✓ Approve
                                                    </button>
                                                    <button
                                                        onClick={() => openModal(service, 'reject')}
                                                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-lg transition-colors"
                                                    >
                                                        ✗ Reject
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-gray-500 text-xs">
                                                    {service.ServiceStatus === '1' ? 'Approved' : 'Rejected'}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] animate-in fade-in duration-300">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-800">
                                {modalAction === 'approve' ? 'Approve Request' : 'Reject Request'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700 text-lg"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="mb-4">
                            <label className="block text-lg font-medium text-gray-700 mb-1">
                                Remarks <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={modalRemarks}
                                onChange={(e) => setModalRemarks(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-lg"
                                rows={4}
                                required
                                placeholder="Enter your remarks..."
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={closeModal}
                                className="px-3 py-2 text-lg text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleStatusChange}
                                className={`px-3 py-2 text-lg text-white rounded-md ${modalAction === 'approve'
                                    ? 'bg-green-500 hover:bg-green-600'
                                    : 'bg-red-500 hover:bg-red-600'
                                    }`}
                            >
                                {modalAction === 'approve' ? 'Approve' : 'Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ServiceRequestAndPermissions;